import React from 'react';

import { ActionIcon, Center, Flex, Menu, Skeleton, Table } from '@mantine/core';
import { IconDots, IconPlus, IconSettings, IconTrash } from '@tabler/icons-react';
import { DroxyStatusBadge, StatusType } from 'components/Droxy/DroxyStatusBadge';
import { NoDataFound } from 'components/NoDataFound';
import { ToolGroupDetails } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolGroupDetails';
import { ToolGroupPreview } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolGroupPreview';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ToolConfig, ToolGroup } from 'shared/interfaces/tools/Tool';

interface ToolsTableProps {
  toolGroups: ToolGroup[];
  deleteToolConfigs: (toolConfigs: ToolConfig[]) => void;
  isLoading: boolean;
}

export function ToolsTable({ toolGroups, deleteToolConfigs, isLoading }: ToolsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleToolGroupConfigure = (toolGroup: ToolGroup) => {
    navigate(`configure/${toolGroup.kind}`, {
      state: { toolConfigs: toolGroup.toolConfigs }
    });
  };

  const handleToolGroupDelete = (toolGroup: ToolGroup) => {
    deleteToolConfigs(toolGroup.toolConfigs);
  };

  const getToolGroupStatus = (toolGroup: ToolGroup): StatusType => {
    if (toolGroup.toolConfigs.length === 0) return 'disabled';
    return toolGroup.toolConfigs.some((config) => config.enabled) ? 'enabled' : 'disabled';
  };

  const renderLoadingRow = () => (
    <tr>
      <td>
        <Skeleton height={20} radius="sm" />
      </td>
      <td>
        <Skeleton height={20} radius="sm" />
      </td>
      <td>
        <Center>
          <Skeleton height={20} radius="sm" />
        </Center>
      </td>
      <td>
        <Flex justify="flex-end" pr="xl">
          <Skeleton height={20} radius="sm" circle />
        </Flex>
      </td>
    </tr>
  );

  const renderEmptyState = () => (
    <tr>
      <td colSpan={4}>
        <NoDataFound
          size="xs"
          pt="xs"
          transparentIconBackground
          icon={<IconPlus size={24} color="gray" />}
          title={t('SMART_ASSISTANT.TOOLS_SECTION.EMPTY_STATE.TITLE')}
          subTitle={t('SMART_ASSISTANT.TOOLS_SECTION.EMPTY_STATE.DESCRIPTION')}
        />
      </td>
    </tr>
  );

  const renderToolGroupRow = (toolGroup: ToolGroup, index: number) => {
    const status = getToolGroupStatus(toolGroup);

    return (
      <tr key={index}>
        <td>
          <ToolGroupPreview toolGroup={toolGroup} />
        </td>
        <td>
          <ToolGroupDetails toolGroup={toolGroup} />
        </td>
        <td>
          <Center>
            <DroxyStatusBadge status={status} />
          </Center>
        </td>
        <td>
          <Flex justify="flex-end" pr="xl">
            <Menu shadow="md" width={200} position="bottom-start">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item icon={<IconSettings size={14} />} onClick={() => handleToolGroupConfigure(toolGroup)}>
                  {t('SMART_ASSISTANT.TOOLS_SECTION.TABLE.CONFIGURE')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item icon={<IconTrash size={14} />} color="red" onClick={() => handleToolGroupDelete(toolGroup)}>
                  {t('SMART_ASSISTANT.TOOLS_SECTION.TABLE.DELETE')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </td>
      </tr>
    );
  };

  return (
    <Table sx={{ tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: '50%' }} />
        <col style={{ width: '30%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '10%' }} />
      </colgroup>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>{t('SMART_ASSISTANT.TOOLS_SECTION.TABLE.NAME')}</th>
          <th style={{ textAlign: 'left' }}>{t('SMART_ASSISTANT.TOOLS_SECTION.TABLE.DETAILS')}</th>
          <th style={{ textAlign: 'center' }}>{t('SMART_ASSISTANT.TOOLS_SECTION.TABLE.STATUS')}</th>
          <th style={{ textAlign: 'right', paddingRight: 16 }}>{t('SMART_ASSISTANT.TOOLS_SECTION.TABLE.ACTIONS')}</th>
        </tr>
      </thead>
      <tbody>
        {isLoading
          ? renderLoadingRow()
          : toolGroups.length === 0
          ? renderEmptyState()
          : toolGroups.map(renderToolGroupRow)}
      </tbody>
    </Table>
  );
}
